/**
 * The visual language.
 *
 * Everything is tuned for one specific viewing condition: **four people on a sofa, three
 * metres from a television.** That drives every choice here — heavy contrast, no thin
 * strokes, no small type, and seat colours that stay distinguishable for the most common
 * forms of colour blindness (they differ in lightness as well as hue, so even fully
 * desaturated they read as four distinct tones).
 */

import { PLAYER_COLORS, REGIONS, SYSTEM_CLASSES } from '../game/constants';
import type { PlayerId, Region, SystemClass } from '../game/types';

/* ---------------------------------------------------------------------- ink */

export const INK = {
  /** Page + canvas backdrop. */
  void: '#07080f',
  voidHigh: '#0d0f1c',
  /** Panel fills, in ascending brightness. */
  panel: 'rgba(14, 17, 30, 0.86)',
  panelSolid: '#0e111e',
  panelHigh: 'rgba(26, 31, 51, 0.92)',
  /** Hairlines and panel borders. */
  edge: 'rgba(126, 148, 196, 0.28)',
  edgeStrong: 'rgba(158, 184, 235, 0.55)',
  /** Type. */
  text: '#e8edf9',
  textDim: '#9aa6c4',
  textFaint: '#5d6684',
  /** Semantic. */
  good: '#5de6a0',
  bad: '#ff6b6b',
  warn: '#ffc74c',
  info: '#6fd3ff',
  /** The "unknown system" glyph colour. */
  unknown: '#39405c',
} as const;

/** Resource accent colours. Also used for the little resource pips on hexes. */
export const RESOURCE_INK = {
  ore: '#d8a45c',
  fuel: '#c08bd8',
  biomass: '#6fd88f',
  data: '#6fb4ff',
} as const;

/** Icon glyphs for resources — a private-use-free set that renders on every platform. */
export const RESOURCE_GLYPH = {
  ore: '◆',
  fuel: '▲',
  biomass: '❋',
  data: '◈',
} as const;

/* -------------------------------------------------------------------- type */

/**
 * Font stack. No web fonts on purpose: a downloaded font is one more thing that can fail
 * offline, and the PWA has to play with no network at all.
 */
export const FONT_STACK =
  '"Eurostile", "Bahnschrift", "DIN Alternate", "Avenir Next Condensed", ' +
  '"Roboto Condensed", "Segoe UI", system-ui, sans-serif';

export const MONO_STACK =
  '"SF Mono", "Cascadia Mono", "JetBrains Mono", "Menlo", ui-monospace, monospace';

/** Type ramp in px at a 1080p reference height; the renderer scales these. */
export const TYPE = {
  hero: 76,
  title: 44,
  heading: 26,
  body: 19,
  label: 15,
  micro: 12.5,
} as const;

export function font(size: number, weight = 600, stack = FONT_STACK): string {
  return `${weight} ${size}px ${stack}`;
}

/* ------------------------------------------------------------------- seats */

export function seatColor(seat: PlayerId): string {
  return PLAYER_COLORS[seat] ?? INK.text;
}

/** Darkened seat colour, for fills behind seat-coloured type. */
export function seatFill(seat: PlayerId, alpha = 0.22): string {
  return withAlpha(seatColor(seat), alpha);
}

/**
 * Seat glyph — a distinct *shape* per seat, drawn alongside the colour.
 *
 * Shape redundancy is the accessibility backstop: if two seat colours are
 * indistinguishable to a given player, the marker outline still differs.
 */
export const SEAT_SHAPE: readonly ('circle' | 'square' | 'triangle' | 'diamond')[] = [
  'circle', 'square', 'triangle', 'diamond',
];

/* ------------------------------------------------------------------ systems */

export interface SystemPaint {
  /** Planet body base. */
  body: string;
  /** Band / atmosphere highlight. */
  accent: string;
  /** Hex plate fill under the planet. */
  plate: string;
  /** Hex plate rim. */
  rim: string;
}

export function systemPaint(cls: SystemClass): SystemPaint {
  const def = SYSTEM_CLASSES[cls];
  return {
    body: def.color,
    accent: def.accent,
    plate: shade(def.color, -0.72),
    rim: shade(def.color, -0.35),
  };
}

export function regionTint(region: Region): string {
  return REGIONS[region].tint;
}

/* -------------------------------------------------------------- colour maths */

/** Parse `#rgb`, `#rrggbb`, or `rgba()` into components. */
export function parseColor(color: string): [number, number, number, number] {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    if (hex.length === 3) {
      return [
        parseInt(hex[0]! + hex[0]!, 16),
        parseInt(hex[1]! + hex[1]!, 16),
        parseInt(hex[2]! + hex[2]!, 16),
        1,
      ];
    }
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
      hex.length >= 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
    ];
  }
  const nums = color.match(/[\d.]+/g);
  if (!nums) return [255, 255, 255, 1];
  return [
    Number(nums[0] ?? 255),
    Number(nums[1] ?? 255),
    Number(nums[2] ?? 255),
    nums[3] !== undefined ? Number(nums[3]) : 1,
  ];
}

export function withAlpha(color: string, alpha: number): string {
  const [r, g, b] = parseColor(color);
  return `rgba(${r | 0}, ${g | 0}, ${b | 0}, ${clamp01(alpha)})`;
}

/** Lighten (`amount > 0`) or darken (`amount < 0`) toward white/black. */
export function shade(color: string, amount: number): string {
  const [r, g, b, a] = parseColor(color);
  const target = amount >= 0 ? 255 : 0;
  const t = Math.abs(amount);
  const mix = (c: number) => Math.round(c + (target - c) * t);
  return `rgba(${mix(r)}, ${mix(g)}, ${mix(b)}, ${a})`;
}

export function mixColor(a: string, b: string, t: number): string {
  const [ar, ag, ab, aa] = parseColor(a);
  const [br, bg, bb, ba] = parseColor(b);
  const k = clamp01(t);
  return `rgba(${Math.round(ar + (br - ar) * k)}, ${Math.round(ag + (bg - ag) * k)}, ` +
    `${Math.round(ab + (bb - ab) * k)}, ${aa + (ba - aa) * k})`;
}

/** Relative luminance, for picking readable text over an arbitrary fill. */
export function luminance(color: string): number {
  const [r, g, b] = parseColor(color);
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Black or white, whichever is readable on `background`. */
export function contrastText(background: string): string {
  return luminance(background) > 0.42 ? '#08090f' : INK.text;
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/* --------------------------------------------------------------- easing/anim */

export const ease = {
  outCubic: (t: number) => 1 - (1 - t) ** 3,
  inOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
  outBack: (t: number) => {
    const c = 1.70158;
    return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2;
  },
  outElastic: (t: number) => {
    if (t === 0 || t === 1) return t;
    return 2 ** (-10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1;
  },
  /** Smooth 0→1→0 pulse, for cursor breathing and highlight throbs. */
  pulse: (t: number) => 0.5 - 0.5 * Math.cos(t * Math.PI * 2),
} as const;

/** Layer z-order, so nothing has to guess draw sequence. */
export const LAYER = {
  starfield: 0,
  regionBands: 10,
  hexPlates: 20,
  hexDetail: 30,
  routes: 40,
  fleets: 50,
  markers: 60,
  cursor: 70,
  particles: 80,
  hud: 90,
  overlay: 100,
} as const;
